import type { Client, Task } from '../../types/crm';
import { taskAccountDisplay } from '../../lib/taskAccountDisplay';
import { formatSingaporeDateTime } from '../../utils/dateTime';
import { taskDisplayStatus, taskStatusModifier } from '../../utils/taskHelpers';

interface TaskListRowProps {
  task: Task;
  client?: Client;
  ownerName: string;
  canEdit: boolean;
  onEdit: (task: Task) => void;
}

export function TaskListRow({ task, client, ownerName, canEdit, onEdit }: TaskListRowProps) {
  const display = taskDisplayStatus(task);
  const done = display === 'Done';
  const account = taskAccountDisplay(task, client);

  return (
    <button
      type="button"
      className={`task-list-row${canEdit ? '' : ' task-list-row--readonly'}`}
      onClick={() => canEdit && onEdit(task)}
      disabled={!canEdit}
    >
      <span className={`task-list-row__status task-status ${taskStatusModifier(display)}`}>
        {display}
      </span>

      <div className="task-list-row__main">
        <span className="task-list-row__title-row">
          <span className={`task-list-row__title${done ? ' task-list-row__title--done' : ''}`}>
            {task.title}
          </span>
          <span
            className={`task-list-row__status-inline task-status ${taskStatusModifier(display)}`}
          >
            {display}
          </span>
        </span>
        {task.due_date && (
          <span className={`task-list-row__due${display === 'Due' ? ' task-list-row__due--overdue' : ''}`}>
            Due {formatSingaporeDateTime(task.due_date)}
          </span>
        )}
      </div>

      <div className="task-list-row__account">
        {client || account.isProspect ? (
          <>
            <span className="task-list-row__account-name">{account.summaryLine}</span>
            {account.motherCode && !account.outletName ? (
              <span className="task-list-row__account-code">{account.motherCode}</span>
            ) : null}
          </>
        ) : (
          <span className="task-list-row__account-code">No account</span>
        )}
      </div>

      <div className="task-list-row__notes">
        {task.description?.trim() ? (
          <span className="task-list-row__notes-text">{task.description.trim()}</span>
        ) : (
          <span className="task-list-row__notes-empty">No notes</span>
        )}
      </div>

      <span className="task-list-row__owner">{ownerName}</span>
      <span className="task-list-row__chevron" aria-hidden>
        ›
      </span>
    </button>
  );
}
